import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef, useMemo } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";

/**
 * FormMoneyMaskedInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
}

const FormMoneyMaskedInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      placeholderChar="_"
      inputRef={refs}
      onAccept={(value: any, input: { unmaskedValue: string }) => {
        if (input.unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={true}
    />
  );
});

/**
 * FormMoneyMaskedInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  required?: boolean,
  readOnly?: boolean,
  largeNumbers?: boolean,
  reactHookForm: UseFormReturn<T>,
};

export const FormMoneyMaskedInput = <T extends {},>({
  name,
  label,
  required,
  readOnly,
  largeNumbers,
  reactHookForm,
}: Props<T>) => {
  const mask = useMemo(() => {
    return largeNumbers ? (
      [
        { mask: 'R$ \\0,\\00' },
        { mask: 'R$ \\0,00' },
        { mask: 'R$ 0,00' },
        { mask: 'R$ 00,00' },
        { mask: 'R$ 000,00' },
        { mask: 'R$ 0.000,00' },
        { mask: 'R$ 00.000,00' },
        { mask: 'R$ 000.000,00' },
        { mask: 'R$ 0.000.000,00' },
        { mask: 'R$ 00.000.000,00' },
        { mask: 'R$ 000.000.000,00' },
      ]
    ) : (
      [
        { mask: 'R$ \\0,\\00' },
        { mask: 'R$ \\0,00' },
        { mask: 'R$ 0,00' },
        { mask: 'R$ 00,00' },
        { mask: 'R$ 000,00' },
        { mask: 'R$ 0.000,00' },
        { mask: 'R$ 00.000,00' },
        { mask: 'R$ 000.000,00' },
      ]
    );
  }, [
    largeNumbers,
  ]);

  const placeholder = useMemo(() => {
    return largeNumbers ? (
      "R$ ___.___.___,__"
    ) : (
      "R$ ___.___,__"
    );
  }, [
    largeNumbers,
  ]);

  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let value = '';
        let helperText: string | null = null;

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        if (typeof field.value === 'string') {
          value = field.value;
        }
        else if (typeof field.value === 'number') {
          value = (Math.round((field.value + Number.EPSILON) * 100)).toString();
        }
        else if (field.value === null) {
          value = '';
        }

        return (
          <FormControl variant="outlined" size="small">
            <InputLabel
              htmlFor={`input-${name as string}`}
              error={helperText !== null}
              shrink={true}
              size="small"
              required={required}>
              {label}
            </InputLabel>

            <OutlinedInput
              {...field}

              onChange={(e) => {
                const rawValue = e.target.value;
                if (!rawValue) {
                  field.onChange(null);
                  return;
                }
                const apenasNumeros = rawValue.replace(/\D/g, '');

                if (!apenasNumeros) {
                  field.onChange(null);
                  return;
                }
                const valorFloat = parseFloat(apenasNumeros) / 100;
                field.onChange(valorFloat);
              }}
              size="small"
              id={`input-${name as string}`}
              name={name as string}
              label={label}
              notched={true}
              readOnly={readOnly}
              required={required}
              disabled={formState.disabled}
              aria-describedby={`${name as string}-component-error-text`}
              inputComponent={FormMoneyMaskedInputBase as any}
              value={
                field.value != null
                  ? Number(field.value).toFixed(2).replace('.', ',')
                  : ''
              }

              inputProps={{
                mask: mask,
                placeholder: placeholder,
                required: required,
              }} />

            <FormHelperText id={`${name as string}-component-error-text`} error={true}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }} />
  );
}